/** Every person with a login, whichever organisation they belong to. */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { request } from '../api/client';
import type { UserList } from '../api/types';
import { day, num, since } from '../components/format';
import { Failed, Loading, PageHead, Panel, Pill } from '../components/ui';

export function Users() {
  const [search, setSearch] = useState('');
  const { data, isPending, error } = useQuery({
    queryKey: ['users'],
    queryFn: () => request<UserList>('/api/v1/admin/users'),
  });

  const needle = search.trim().toLowerCase();
  const rows = (data?.users ?? []).filter(
    (row) =>
      !needle ||
      row.email.toLowerCase().includes(needle) ||
      row.full_name.toLowerCase().includes(needle) ||
      (row.organisation_name ?? '').toLowerCase().includes(needle),
  );

  return (
    <>
      <PageHead
        eyebrow="Users"
        title="Everybody who can sign in"
        note={data ? `${num(data.users.length)} accounts. Operators are marked.` : undefined}
      />
      <div className="toolbar">
        <input
          type="search"
          placeholder="Search name, email or customer…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ minWidth: 300 }}
          aria-label="Search users"
        />
      </div>
      {isPending ? <Loading /> : null}
      {error ? <Failed error={error} fallback="Could not load users." /> : null}
      {data ? (
        <Panel flush>
          {rows.length === 0 ? (
            <div className="panel-body muted">Nobody matches that.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Customer</th>
                  <th>Role</th>
                  <th>Standing</th>
                  <th>Last seen</th>
                  <th className="numeric">Devices</th>
                  <th className="numeric">Conversations</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.user_id}>
                    <td>
                      {row.full_name || <span className="faint">no name</span>}
                      {row.is_platform_admin ? (
                        <span className="pill ok" style={{ marginLeft: 8 }}>
                          operator
                        </span>
                      ) : null}
                      <div className="muted small">{row.email}</div>
                    </td>
                    <td>
                      {row.organisation_id ? (
                        <Link to={`/customers/${row.organisation_id}`}>
                          {row.organisation_name}
                        </Link>
                      ) : (
                        <span className="faint">none</span>
                      )}
                    </td>
                    <td>
                      <Pill value={row.role} />
                    </td>
                    <td>
                      <Pill value={row.account_status} />
                    </td>
                    <td>{since(row.last_seen_at)}</td>
                    <td className="numeric">{row.active_devices}</td>
                    <td className="numeric">
                      {row.conversations ? (
                        <Link to={`/conversations?user_id=${row.user_id}`}>
                          {num(row.conversations)}
                        </Link>
                      ) : (
                        <span className="muted">0</span>
                      )}
                    </td>
                    <td className="muted">{day(row.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      ) : null}
    </>
  );
}
